<template>
  <div class="fm-page-shell">
    <!-- ===== CONTEXTUAL RAIL ===== -->
    <aside class="fm-rail">
      <div class="fm-rail-label">Services</div>
      <select class="fm-rail-select" v-model="agentFilter">
        <option value="all">All agents</option>
        <option v-for="name in agentFilterOptions" :key="name" :value="name">{{ name }}</option>
      </select>

      <button type="button" class="fm-view" :class="{ 'fm-view--active': statusFilter === 'all' }" @click="setStatus('all')">
        <span class="fm-view-dot" style="background:#94A3B8"></span>All
        <span class="fm-view-count">{{ statusCounts.all }}</span>
      </button>
      <button type="button" class="fm-view" :class="{ 'fm-view--active': statusFilter === 'active' }" @click="setStatus('active')">
        <span class="fm-view-dot" style="background:#10B981"></span>Active
        <span class="fm-view-count">{{ statusCounts.active }}</span>
      </button>
      <button type="button" class="fm-view" :class="{ 'fm-view--active': statusFilter === 'inactive' }" @click="setStatus('inactive')">
        <span class="fm-view-dot" style="background:#EF4444"></span>Inactive
        <span class="fm-view-count">{{ statusCounts.inactive }}</span>
      </button>

      <template v-if="typeFacets.length">
        <div class="fm-rail-sublabel">Type</div>
        <button type="button" class="fm-view" :class="{ 'fm-view--active': typeFilter === 'all' }" @click="setType('all')">
          <span class="fm-view-dot" style="background:#94A3B8"></span>All types
          <span class="fm-view-count">{{ statusCounts.all }}</span>
        </button>
        <button v-for="t in typeFacets" :key="t.key" type="button" class="fm-view"
                :class="{ 'fm-view--active': typeFilter === t.key }" @click="setType(t.key)">
          <span class="fm-view-dot" style="background:#3B82F6"></span>{{ t.key }}
          <span class="fm-view-count">{{ t.count }}</span>
        </button>
      </template>
    </aside>

    <!-- ===== MAIN ===== -->
    <main class="fm-main">
      <b-overlay :show="loading" spinner small>
        <div class="fm-page">
          <div class="fm-page-head">
            <div class="fm-page-title">Services</div>
            <button class="fm-btn fm-btn--primary" @click="showAddServiceModal"><i class="fa-solid fa-plus"></i>Add Service</button>
          </div>
          <div class="fm-page-desc">View and manage secure services and remote endpoints for your environment.</div>

          <div class="fm-toolbar">
            <div class="fm-search">
              <i class="fa-solid fa-magnifying-glass"></i>
              <b-form-input type="search" v-model="searchQuery" placeholder="Search services by name..."></b-form-input>
            </div>
            <div v-if="filteredServices.length > perPage" class="fm-toolbar-pager">
              <b-pagination
                v-model="currentPage"
                :total-rows="filteredServices.length"
                :per-page="perPage"
                size="sm"
              ></b-pagination>
            </div>
          </div>

          <div v-if="pagedServices.length > 0" class="fm-list">
            <div v-for="service in pagedServices" :key="service.ServiceName" class="fm-row" @click="openService(service)">
              <div class="fm-row-main">
                <div class="fm-row-top">
                  <span class="fm-row-name">{{ service.ServiceName }}</span>
                  <i class="fa-regular fa-copy fm-row-copy"></i>
                  <span v-if="service.ServiceType" class="fm-tag">{{ service.ServiceType }}</span>
                  <span v-if="service.ServiceSubType" class="fm-tag">{{ service.ServiceSubType }}</span>
                </div>
                <div class="fm-row-desc">Agent · {{ service.AgentName }}</div>
              </div>
              <span class="fm-value-pill">{{ service.Host }}:{{ service.Port }}</span>
              <div class="fm-status-cell">
                <span class="fm-status-pill" :class="service.IsActive ? 'fm-status-pill--suc' : 'fm-status-pill--err'">
                  <span class="fm-dot"></span>{{ service.IsActive ? 'Active' : 'Stopped' }}
                </span>
              </div>
              <button type="button" class="fm-toggle" :class="{ 'fm-toggle--on': service.IsActive }"
                      @click.stop="toggleServiceStatus(service)">
                <span class="fm-toggle-knob"></span>
              </button>
            </div>
          </div>
          <div v-else-if="services.length > 0" class="fm-empty">
            No services match these filters.
          </div>
          <div v-else class="fm-empty">
            No services started yet.
            <div class="mt-3"><button class="fm-btn fm-btn--primary" @click="showAddServiceModal"><i class="fa-solid fa-plus"></i>Add New</button></div>
          </div>

        </div>

        <!-- Add Service Modal -->
        <b-modal v-model="showModal" title="Add Service" hide-footer>
          <b-form @submit.stop.prevent="handleExposeService">
            <div class="row">
              <div class="col-md-12">
                <b-form-group label="Agent" label-for="agent-select">
                  <b-form-select id="agent-select" v-model="newService.agent" :options="agentOptions"
                                 required></b-form-select>
                </b-form-group>
              </div>

              <div class="col-md-4">
                <b-form-group label="Type" label-for="type">
                  <b-form-select
                    id="type"
                    v-model="newService.type"
                    :options="serviceTypes"
                    required
                  ></b-form-select>
                </b-form-group>
              </div>

              <div class="col-md-8">
                <b-form-group label="Subtype" label-for="subtype">
                  <b-form-select
                    id="subtype"
                    v-model="newService.subtype"
                    :options="getSubtypes(newService.type)"
                    required
                  ></b-form-select>
                </b-form-group>
              </div>

              <div class="col-md-12">
                <b-form-group label="Name" label-for="service-name">
                  <b-form-input id="service-name" v-model="newService.name" required></b-form-input>
                </b-form-group>
              </div>
              <div class="col-md-12">
                <b-form-group label="Host" label-for="service-host">
                  <b-form-input id="service-host" v-model="newService.host" required></b-form-input>
                </b-form-group>
              </div>
              <div class="col-md-12">
                <b-form-group label="Port" label-for="service-port">
                  <b-form-input type="number" id="service-port" v-model="newService.port" required></b-form-input>
                </b-form-group>
              </div>
            </div>
            <div class="col-md-12 mt-2 mb-5 d-flex align-items-center">
              <b-form-checkbox v-model="newService.autoReconnect">
                Auto Reconnect
              </b-form-checkbox>
              <b-tooltip target="reconnect-tooltip" placement="right">
                The service will try to reconnect when agents reconnect.
              </b-tooltip>
              <i id="reconnect-tooltip" class="fa fa-question-circle ml-2" aria-hidden="true"></i>
            </div>
            <div class="text-center">
              <b-button class="btn-fill" type="submit" variant="primary">Create</b-button>
            </div>
          </b-form>
        </b-modal>

        <!-- Delete Service Modal -->
        <b-modal v-model="showDeleteModal" title="Delete Service" hide-footer>
          <b-form @submit.stop.prevent="confirmDeleteService">
            <p>Are you sure you want to delete the service "{{ serviceToDelete.ServiceName }}"?</p>
            <b-form-group label="Enter service name to confirm" label-for="confirm-service-name">
              <b-form-input id="confirm-service-name" v-model="confirmServiceName" required></b-form-input>
            </b-form-group>
            <div class="text-center">
              <b-button type="submit" class="btn btn-fill btn-danger" variant="danger"
                        :disabled="confirmServiceName !== serviceToDelete.ServiceName">Delete Service
              </b-button>
              <b-button class="btn btn-fill btn-secondary" variant="secondary" @click="showDeleteModal = false">Cancel
              </b-button>
            </div>
          </b-form>
        </b-modal>
      </b-overlay>
    </main>

    <!-- ===== DETAIL DRAWER ===== -->
    <detail-drawer
      :open="drawerOpen"
      kind="Service"
      :title="selected.ServiceName || ''"
      :status="selected.IsActive ? 'Active' : 'Stopped'"
      :tone="selected.IsActive ? 'suc' : 'err'"
      :fields="drawerFields"
      @close="closeDrawer">
      <template #footer>
        <button class="fm-btn fm-btn--danger" @click="drawerAction('toggle')">
          <i class="fa-solid" :class="selected.IsActive ? 'fa-stop' : 'fa-play'"></i>{{ selected.IsActive ? 'Stop' : 'Start' }}
        </button>
        <button class="fm-btn fm-btn--ghost" @click="drawerAction('delete')">
          <i class="fa-solid fa-trash"></i>Delete
        </button>
      </template>
    </detail-drawer>
  </div>
</template>

<script>
import apiClient from '@/http';
import DetailDrawer from '@/components/DetailDrawer.vue';
import {BModal, BForm, BFormGroup, BFormInput, BButton, BOverlay, BFormSelect, BButtonGroup} from 'bootstrap-vue';

export default {
  components: {
    DetailDrawer,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BOverlay,
    BFormSelect,
    BButtonGroup,
  },
  data() {
    return {
      loading: false,
      searchQuery: '',
      showModal: false,
      showDeleteModal: false,
      agents: [],
      services: [],
      viewMode: 'cards', // Default view mode is cards
      newService: {
        agent: '',
        name: '',
        host: '',
        port: null,
        serviceType: '',
        serviceSubType: '',
        autoReconnect: false,
      },
      serviceToDelete: {},
      statusFilter: 'all',
      typeFilter: 'all',
      agentFilter: 'all',
      currentPage: 1,
      perPage: 10,
      confirmServiceName: '',
      drawerOpen: false,
      selected: {},
      serviceTypes: [
        {value: 'database', text: 'Database'},
        {value: 'cluster', text: 'Cluster'},
        {value: 'tcp', text: 'TCP'},
        {value: 'server', text: 'Server'},
        // Add more types as needed
      ],
      tableColumns: ['Service Name', 'Agent Name', 'Host', 'Port', 'Service Type', 'Application', 'Auto Connect', 'StartedBy'],
    };
  },
  computed: {
    // Services after the free-text search (facet counts are based on this set)
    searchedServices() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return this.services;
      return this.services.filter(s => (s.ServiceName || '').toLowerCase().includes(q));
    },
    // Search + agent, before status/type — the pool the facet counts describe
    scopedServices() {
      let list = this.searchedServices;
      if (this.agentFilter !== 'all') {
        list = list.filter(s => s.AgentName === this.agentFilter);
      }
      return list;
    },
    statusCounts() {
      const list = this.scopedServices;
      return {
        all: list.length,
        active: list.filter(s => s.IsActive).length,
        inactive: list.filter(s => !s.IsActive).length,
      };
    },
    typeFacets() {
      const counts = {};
      this.scopedServices.forEach(s => {
        const t = s.ServiceType;
        if (!t) return;
        counts[t] = (counts[t] || 0) + 1;
      });
      return Object.keys(counts).sort().map(key => ({ key, count: counts[key] }));
    },
    filteredServices() {
      let list = this.scopedServices;
      if (this.statusFilter === 'active') list = list.filter(s => s.IsActive);
      else if (this.statusFilter === 'inactive') list = list.filter(s => !s.IsActive);
      if (this.typeFilter !== 'all') list = list.filter(s => s.ServiceType === this.typeFilter);
      return list;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredServices.length / this.perPage));
    },
    pagedServices() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredServices.slice(start, start + this.perPage);
    },
    agentFilterOptions() {
      const names = new Set(this.services.map(s => s.AgentName).filter(Boolean));
      return Array.from(names).sort();
    },
    agentOptions() {
      return this.agents
        .map((agent) => ({value: agent.Name, text: agent.Name}));
    },
    drawerFields() {
      const s = this.selected;
      if (!s || !s.ServiceName) return [];
      return [
        {label: 'Status', value: s.IsActive ? 'Active' : 'Stopped'},
        {label: 'Agent', value: s.AgentName},
        {label: 'Type', value: s.ServiceType},
        {label: 'Application', value: s.ServiceSubType},
        {label: 'Host', value: s.Host, mono: true},
        {label: 'Port', value: String(s.Port), mono: true},
        {label: 'Auto Reconnect', value: s.AutoReconnect ? 'Yes' : 'No'},
        {label: 'Started By', value: s.StartedBy},
      ];
    },
  },
  watch: {
    // Any filter/search change returns to the first page of the filtered view
    searchQuery() { this.currentPage = 1; },
    statusFilter() { this.currentPage = 1; },
    typeFilter() { this.currentPage = 1; },
    agentFilter() { this.currentPage = 1; },
  },
  methods: {
    setStatus(status) {
      this.statusFilter = status;
    },
    setType(type) {
      this.typeFilter = type;
    },
    openService(service) {
      this.selected = service;
      this.drawerOpen = true;
    },
    closeDrawer() {
      this.drawerOpen = false;
    },
    drawerAction(type) {
      const svc = this.selected;
      this.closeDrawer();
      if (type === 'toggle') {
        this.toggleServiceStatus(svc);
      } else if (type === 'delete') {
        this.showDeleteServiceModal(svc);
      }
    },
    showAddServiceModal() {
      this.showModal = true;
    },
    getSubtypes(type) {
      // Return appropriate subtypes based on the type
      if (type === 'database') {
        return [
          {value: 'mysql', text: 'MySQL'},
          {value: 'postgres', text: 'PostgreSQL'},
          // Add more subtypes as needed
        ];
      } else if (type === 'cluster') {
        return [
          {value: 'k8s', text: 'Kubernetes'},
          // Add more subtypes as needed
        ];
      } else if (type === 'server') {
        return [
          {value: 'ssh', text: 'SSH Service'},
          // Add more subtypes as needed
        ];
      } else {
        return [
          {value: 'tcp', text: 'TCP Service'},
          // Add more subtypes as needed
        ];
      }
      return [];
    },
    showDeleteServiceModal(service) {
      this.serviceToDelete = service;
      this.confirmServiceName = '';
      this.showDeleteModal = true;
    },
    async fetchAgents() {
      this.loading = true;
      try {
        const response = await apiClient.get('/agents');
        this.agents = response.data ? response.data : [];
      } catch (error) {
        const errorMessage = error.response && error.response.data ? error.response.data : 'Error fetching agents';
        this.showNotification(errorMessage, 'danger');
      } finally {
        this.loading = false;
      }
    },
    async fetchServices() {
      // The API paginates all services and derives Active/Inactive from live
      // network state, so it can't filter by status server-side. We load the
      // full set once and do status/type/agent filtering + paging client-side,
      // which keeps the rail counts and pager accurate for the current view.
      this.loading = true;
      try {
        const pageSize = 100;
        let page = 1;
        let totalPages = 1;
        const all = [];
        do {
          const response = await apiClient.get('/services', { params: { page, pageSize } });
          const data = response.data || {};
          if (data.services) all.push(...data.services);
          totalPages = (data.pagination && data.pagination.totalPages) || 1;
          page += 1;
        } while (page <= totalPages);
        this.services = all;
        if (this.currentPage > this.totalPages) this.currentPage = this.totalPages;
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.services = [];
        } else {
          const errorMessage = error.response && error.response.data ? error.response.data : 'Error fetching services';
          this.showNotification(errorMessage, 'danger');
        }
      } finally {
        this.loading = false;
      }
    },
    async handleExposeService() {
      const selectedAgent = this.agents.find((agent) => agent.Name === this.newService.agent);

      if (!selectedAgent) {
        this.showNotification('Selected agent not found', 'danger');
        return;
      }

      const serviceRequest = {
        ServiceName: this.newService.name,
        AgentName: selectedAgent.Name,
        AgentToken: selectedAgent.AgentToken,
        Host: this.newService.host,
        Port: this.newService.port,
        ServiceType: this.newService.type,
        ServiceSubType: this.newService.subtype,
        AutoReconnect: this.newService.autoReconnect,
      };

      try {
        await apiClient.put('/service', serviceRequest);
        this.newService = {agent: '', name: '', host: '', port: null, autoReconnect: false};
        this.showModal = false;
        this.showNotification('Service created successfully', 'success');
      } catch (error) {
        const errorMessage = error.response && error.response.data ? error.response.data : 'Error starting service';
        this.showNotification(errorMessage, 'danger');
      }

      await this.fetchServices(); // Refresh the service list after adding a new service
    },
    async toggleServiceStatus(service) {
      const action = service.IsActive ? 'deactivate' : 'activate';

      try {
        await apiClient.post(`/service/${service.ServiceName}/actions`, {
          action,
        });

        this.showNotification(`Service ${action}d successfully`, 'success');
      } catch (error) {
        const errorMessage = error.response && error.response.data ? error.response.data : `Error ${action}ing service`;
        this.showNotification(errorMessage, 'danger');
      }

      await this.fetchServices(); // Refresh the service list after changing the service status
    },
    async confirmDeleteService() {
      try {
        if (this.serviceToDelete.IsActive) {
          await this.toggleServiceStatus(this.serviceToDelete); // Deactivate the service before deleting
        }

        const response = await apiClient.delete(`/service/${this.serviceToDelete.ServiceName}`);
        if (response.status === 200) {
          this.showNotification('Service deleted successfully', 'success');
        } else if (response.status === 403) {
          this.showNotification('Forbidden: You are not an admin user', 'danger');
        }
        ``

        this.showDeleteModal = false;
        await this.fetchServices(); // Refresh the service list after deleting a service
      } catch (error) {
        const errorMessage = error.response && error.response.data ? error.response.data : 'Error deleting service';
        this.showNotification(errorMessage, 'danger');
      }
    },
    showNotification(message, type) {
      this.$bvToast.toast(message, {
        title: `Info`,
        variant: type,
        solid: true
      })

    },
  },
  mounted() {
    this.fetchAgents();
    this.fetchServices();
  },
};
</script>

<style scoped>
.content {
  padding: 20px;
}

/* Metric Cards */
.metric-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  min-height: 120px;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
}

.metric-card--primary {
  border-left: 4px solid #667eea;
}

.metric-card--success {
  border-left: 4px solid #10B981;
}

.metric-card--info {
  border-left: 4px solid #06B6D4;
}

.metric-card--warning {
  border-left: 4px solid #F59E0B;
}

.metric-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  font-size: 24px;
  color: white;
}

.metric-card--primary .metric-icon {
  background: linear-gradient(135deg, #667eea, #764ba2);
}

.metric-card--success .metric-icon {
  background: linear-gradient(135deg, #10B981, #059669);
}

.metric-card--info .metric-icon {
  background: linear-gradient(135deg, #06B6D4, #0891b2);
}

.metric-card--warning .metric-icon {
  background: linear-gradient(135deg, #F59E0B, #D97706);
}

.metric-content {
  flex: 1;
}

.metric-value {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  color: #2c3e50;
}

.metric-label {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: #4a5568;
}

.metric-subtitle {
  color: #718096;
  font-size: 0.9rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .metric-card {
    padding: 16px;
    min-height: 100px;
  }
  
  .metric-icon {
    width: 50px;
    height: 50px;
    margin-right: 16px;
    font-size: 20px;
  }
  
  .metric-value {
    font-size: 2rem;
  }
}

.card-title {
  font-size: 24px;
  font-weight: bold;
}

.btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-fill {
  margin-right: 5px;
}

.text-center {
  text-align: center;
}

.py-5 {
  padding-top: 3rem;
  padding-bottom: 3rem;
}

.b-modal {
  max-width: 500px;
}

.clickable-icon {
  cursor: pointer;
  padding: 5px;
}

.clickable-icon:hover {
  color: #007bff; /* Change color on hover if needed */
}

/* Card View Styles */
.card-view-container {
  margin-top: 10px;
}

.service-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border-radius: 8px;
  overflow: hidden;
}

.service-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.service-card .card-title {
  font-size: 16px;
  font-weight: bold;
  max-width: 70%;
}

.active-service {
  border-top: 3px solid #4CAF50;
}

.inactive-service {
  border-top: 3px solid #F44336;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
}

.status-badge.active {
  background-color: rgba(76, 175, 80, 0.15);
  color: #4CAF50;
}

.status-badge.inactive {
  background-color: rgba(244, 67, 54, 0.15);
  color: #F44336;
}

.service-card-content {
  padding: 10px 0;
}

.service-card-details p {
  margin-bottom: 8px;
  font-size: 13px;
  color: #666;
}

.service-card-details strong {
  color: #333;
}

@media (max-width: 1200px) {
  .col-md-3 {
    flex: 0 0 33.333333%;
    max-width: 33.333333%;
  }
}

@media (max-width: 992px) {
  .col-md-3 {
    flex: 0 0 50%;
    max-width: 50%;
  }
}

@media (max-width: 576px) {
  .col-md-3 {
    flex: 0 0 100%;
    max-width: 100%;
  }
}
</style>

